import { Listbox } from '@headlessui/react';
import { memo } from 'react';
import { classnames } from '@/libs/utils';
import Icon from '@/components/atoms/Icon';

interface ShapeOption {
  icon: React.ReactNode;
  value: string;
  label: string;
  borderRadius: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
}

const shapes: ShapeOption[] = [
  {
    icon: <Icon name="IconSpeakerLabelRounded" size={16} />,
    value: 'rounded',
    label: 'Rounded',
    borderRadius: '0.5rem'
  },
  {
    icon: <Icon name="IconSpeakerLabelSquare" size={16} />,
    value: 'rectangle',
    label: 'Rectangular',
    borderRadius: '0'
  },
  { icon: <Icon name="IconPill" size={16} />, value: 'pill', label: 'Pill', borderRadius: '100cqh' },
  {
    icon: <Icon name="IconTransparent" size={16} />,
    value: 'none',
    label: 'No Background',
    borderRadius: '0.5rem'
  }
];

const SpeakerLabelShapeSelector = memo(({ value, onChange }: Props) => {
  const selectedShape = shapes.find(shape => shape.value === value) || shapes[0];

  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <>
          <Listbox.Button
            data-testid="shape-selector-button"
            className="flex h-8 w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-card-active-border"
          >
            <span className="flex items-center gap-2 truncate">
              {selectedShape.icon}
              {selectedShape.label}
            </span>
            <Icon
              name="IconChevronDown"
              size={16}
              className={classnames('transition-transform duration-200', open ? 'rotate-180' : '')}
            />
          </Listbox.Button>
          <Listbox.Options className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none">
            {shapes.map(shape => (
              <Listbox.Option
                key={shape.value}
                value={shape.value}
                data-testid={`shape-option-${shape.value}`}
                className="cursor-pointer select-none px-3 py-2 hover:bg-gray-50"
              >
                {({ selected }) => (
                  <div className="flex items-center justify-between">
                    <span
                      className={classnames(
                        'flex items-center gap-2 text-sm',
                        selected ? 'font-semibold text-card-active-border' : 'text-gray-700'
                      )}
                    >
                      {shape.icon}
                      {shape.label}
                    </span>
                    {selected && <Icon name="IconCheck" size={16} className="text-card-active-border" />}
                  </div>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </>
      )}
    </Listbox>
  );
});

export default SpeakerLabelShapeSelector;
